// AST for the s-expression DSL. Spec: SEXPR_ARCHITECTURE.md §4 (lexical and
// syntactic specification) and §11.1 (core type definitions). Every node
// carries a SourcePos populated by the parser; downstream consumers
// (type-checker, evaluator, error reporter) treat positions as mandatory.

export type SourcePos = {
  readonly line: number; //   1-based line number of the first character
  readonly col: number; //    1-based column number of the first character
  readonly offset: number; // 0-based byte offset of the first character
  readonly length: number; // byte length of the token range
};

/**
 * Sentinel position for diagnostics that cannot be attributed to a source
 * span — typically because the input was a direct JS object rather than a
 * parsed string, or because the error is structural and predates parsing
 * (e.g., empty script).
 */
export const NO_POS: SourcePos = { line: 0, col: 0, offset: 0, length: 0 };

export type LiteralNode = {
  readonly kind: 'literal';
  readonly value: number | string | boolean | null;
  readonly pos: SourcePos;
};

export type SymbolNode = {
  readonly kind: 'symbol';
  readonly name: string;
  readonly pos: SourcePos;
};

export type AstArg =
  | { readonly kind: 'positional'; readonly value: AstNode }
  | {
      readonly kind: 'kwarg';
      readonly key: string;
      readonly keyPos: SourcePos;
      readonly value: AstNode;
    };

export type ListNode = {
  readonly kind: 'list';
  readonly op: string; //         head symbol, separated from args at parse time
  readonly opPos: SourcePos; //   position of the head symbol (for op-targeted diagnostics)
  readonly args: readonly AstArg[];
  readonly pos: SourcePos; //     span covering the opening ( through the closing )
};

export type VectorNode = {
  readonly kind: 'vector';
  readonly elements: readonly AstNode[];
  readonly pos: SourcePos; //     span covering the opening [ through the closing ]
};

export type AstNode = LiteralNode | SymbolNode | ListNode | VectorNode;
